/**
 * GitHub adapter for the Dependencies card: the facts the API reads from the repository's manifests
 * and deps.dev, turned into the counts and the worst-first list the card shows.
 *
 * The prototype's other two readers here, "progress" and "what we're working on now", are not ported:
 * both are computed from a snapshot fixture rather than from GitHub, so there is nothing behind them.
 * Colour comes from the central threshold config, never from here.
 */
import i18n from '@app/i18n';
import { ProductDependencies, ProductDependency } from '@data/utils/types';
import { DependenciesMetric, DependencyPackage } from './types';
import {
  DependencyCurrency,
  dependenciesCountStatus,
  dependencyCurrency,
  hasNoNewerStable,
  isAheadOfLatest,
  isPatchBehindOnly,
} from './thresholds';
import { durationSince, versionCore, versionLineLabel } from './util';

// Longest behind first; a package with no newer release to date it by sorts after every dated one.
const behindSinceTime = (dependency: ProductDependency): number =>
  dependency.behindSince ? Date.parse(dependency.behindSince) : Number.POSITIVE_INFINITY;

type GradedCurrency = Exclude<DependencyCurrency, 'unknown'>;

const GROUP_RANK: Record<GradedCurrency, number> = { unsupported: 0, behind: 1, current: 2 };

/** How far behind, counted in lines as `versionLine` draws them: majors from 1.x, the first non-zero segment for 0.x. */
const behindFact = (dependency: ProductDependency): string => {
  const majors = dependency.majorsBehind ?? 0;
  if (majors >= 1) return i18n.t('productDashboard.statusMajorsBehind', { count: majors });
  const inUseLine = versionLineLabel(dependency.versionInUse);
  const latest = dependency.latestVersion ?? '';
  if (inUseLine?.startsWith('0.') && inUseLine !== versionLineLabel(latest)) {
    return i18n.t('productDashboard.statusNewerZeroLine');
  }
  const [inUseCore, latestCore] = [versionCore(dependency.versionInUse), versionCore(latest)];
  return inUseCore && latestCore && inUseCore[1] === latestCore[1]
    ? i18n.t('productDashboard.statusPatchesOnly')
    : i18n.t('productDashboard.statusMinorsOnly');
};

/** Why it is unsupported, or how far behind it is: a fact, with no judgement word. */
const statusOf = (dependency: ProductDependency, currency: GradedCurrency, now: Date): string => {
  if (dependency.isLatestDeprecated) return i18n.t('productDashboard.statusPackageDeprecated');
  if (dependency.isDeprecated) return i18n.t('productDashboard.statusVersionDeprecated');
  const line = versionLineLabel(dependency.versionInUse);
  if (currency === 'unsupported' && line && dependency.lineLastReleasedAt) {
    const duration = durationSince(dependency.lineLastReleasedAt, now);
    return i18n.t('productDashboard.statusNoLineRelease', { line, duration });
  }
  if (isAheadOfLatest(dependency)) return i18n.t('productDashboard.statusAhead');
  if (hasNoNewerStable(dependency)) return i18n.t('productDashboard.statusNoNewerStable');
  if (currency === 'current' && !isPatchBehindOnly(dependency)) return i18n.t('productDashboard.statusLatest');
  return behindFact(dependency);
};

const toPackage = (dependency: ProductDependency, currency: GradedCurrency, now: Date): DependencyPackage => {
  // Nothing to move to when on the latest or when the whole package is deprecated: no arrow and no time behind.
  const hasTarget = (currency !== 'current' || isPatchBehindOnly(dependency)) && !dependency.isLatestDeprecated;
  return {
    name: dependency.name,
    versionInUse: dependency.versionInUse,
    latestVersion: hasTarget ? (dependency.latestVersion ?? dependency.versionInUse) : dependency.versionInUse,
    status: statusOf(dependency, currency, now),
    newerOutFor: hasTarget && dependency.behindSince ? durationSince(dependency.behindSince, now) : '',
    usedAt:
      dependency.directVersionCount > 1
        ? i18n.t('productDashboard.usedAtVersions', { count: dependency.directVersionCount })
        : '',
    currency,
  };
};

export const github = {
  // Which unknown the card is showing. Nothing to read and nothing read today are both blank donuts,
  // and only the second one is worth coming back for.
  noDataKeyFor(data?: ProductDependencies): string {
    if (data?.isAllArchived) return 'productDashboard.dependenciesAllArchived';
    if (data?.isUnreachable) return 'productDashboard.dependenciesUnavailable';
    // Packages were found but none could be graded, which is not the same as nothing being connected.
    if (data?.uncheckedCount) return 'productDashboard.dependenciesNoneChecked';
    return 'productDashboard.dependenciesNoData';
  },

  getDependenciesFrom(data: ProductDependencies, now: Date = new Date()): DependenciesMetric | null {
    if (!data.isTracked || !data.dependencies.length) return null;

    const graded = data.dependencies.map(dependency => ({ dependency, currency: dependencyCurrency(dependency, now) }));
    // Every package unknown to the registry means nothing was actually graded, so this is the no-data
    // case rather than a clean one. Private-only inventories would otherwise score a green donut.
    if (!graded.some(item => item.currency !== 'unknown')) return null;
    const counts = {
      packagesUsed: graded.filter(item => item.currency !== 'unknown').length,
      current: graded.filter(item => item.currency === 'current').length,
      behind: graded.filter(item => item.currency === 'behind').length,
      unsupported: graded.filter(item => item.currency === 'unsupported').length,
    };

    return {
      ...counts,
      status: dependenciesCountStatus(counts),
      checkedAt: data.checkedAt,
      uncheckedCount: data.uncheckedCount,
      // Unsupported first, so the list opens on what actually needs doing.
      packages: graded
        .flatMap(item => (item.currency === 'unknown' ? [] : [{ ...item, currency: item.currency }]))
        // Group first, so a deprecated package with no newer release is not buried at the bottom.
        .sort(
          (a, b) =>
            GROUP_RANK[a.currency] - GROUP_RANK[b.currency] ||
            behindSinceTime(a.dependency) - behindSinceTime(b.dependency) ||
            a.dependency.name.localeCompare(b.dependency.name),
        )
        .map(item => toPackage(item.dependency, item.currency, now)),
    };
  },
};
